// @ts-nocheck
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { RichTextDisplay } from "@/design-system/forms/RichTextEditor";
import EventVerificationNotice from "@/components/events/EventVerificationNotice";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { goToRecruitLook } from "@/lib/recruitlookHandoff";
import { registrationTeamId, resolveRegistrationTeamNames, storedRegistrationTeamName } from "@/lib/rlTeams";

const sb = supabase as any;

export default function RegistrationSuccess() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const cartId = params.get("cart_id");
  const sessionId = params.get("session_id");
  const paymentError = params.get("payment_error");
  const fromStripe = !!sessionId;

  const [pollState, setPollState] = useState<"polling" | "paid" | "pending" | "idle">(
    fromStripe ? "polling" : "idle"
  );

  const { data: registrations, isLoading, refetch } = useQuery({
    queryKey: ["reg-success", cartId],
    enabled: !!cartId,
    queryFn: async () => {
      const { data, error } = await sb
        .from("event_registrations_v2")
        .select(
          "id,team_id,recruitlook_team_id,display_name_override,payment_status,payment_method_chosen,payment_due_at,total_amount_due,total_amount_paid,processing_fee,payment_promise_notes,extras_summary,special_requests_fee,extras_total,scheduling_tier,scheduling_tier_fee,discount_total,pool,events(id,name,slug,pay_later_instructions,pay_later_late_fee)"
        )
        .eq("registration_cart_id", cartId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      const rows = data ?? [];
      const names = await resolveRegistrationTeamNames(rows);
      return rows.map((row: any) => ({
        ...row,
        resolved_team_name: names[registrationTeamId(row)] || storedRegistrationTeamName(row),
      }));
    },
  });

  // Verify the Stripe session directly — no waiting on the webhook.
  // Short DB fallback (max ~6s) only if Stripe verification is inconclusive.
  useEffect(() => {
    if (!fromStripe || !cartId) return;
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | undefined;

    (async () => {
      try {
        const { data } = await invokeEdgeFunction("verify-registration-session", {
          body: { session_id: sessionId },
        });
        if (cancelled) return;
        if (data?.paid) {
          setPollState("paid");
          refetch();
          return;
        }
      } catch {
        // fall through to the short DB check
      }
      if (cancelled) return;

      let elapsed = 0;
      const max = 6000;
      interval = setInterval(async () => {
        elapsed += 2000;
        const { data } = await sb
          .from("event_registrations_v2")
          .select("payment_status")
          .eq("registration_cart_id", cartId);
        const onlineRegs = (data ?? []).filter((r: any) => r.payment_status !== null);
        const allPaid =
          onlineRegs.length > 0 &&
          onlineRegs.every(
            (r: any) => r.payment_status === "paid" || r.payment_status === "comped"
          );
        if (allPaid) {
          setPollState("paid");
          clearInterval(interval);
          refetch();
        } else if (elapsed >= max) {
          setPollState("pending");
          clearInterval(interval);
          refetch();
        }
      }, 2000);
    })();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [fromStripe, cartId, sessionId, refetch]);


  const showSpinner = fromStripe && pollState === "polling";
  const showPending = fromStripe && pollState === "pending";

  return (
    <div className="min-h-screen bg-background py-10">
      <div className="container max-w-2xl mx-auto px-4">
        <div className="text-center mb-6">
          {showSpinner ? (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-3 animate-spin" />
              <h1 className="text-3xl font-bold">Confirming your payment…</h1>
              <p className="text-muted-foreground mt-2">
                Checking with Stripe — this usually takes a second.
              </p>
            </>
          ) : paymentError ? (
            <>
              <AlertCircle className="w-16 h-16 text-amber-600 mx-auto mb-3" />
              <h1 className="text-3xl font-bold">Registration saved — payment not completed</h1>
              <p className="text-muted-foreground mt-2">
                You can finish payment from your RecruitLook dashboard at any time.
              </p>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
              <h1 className="text-3xl font-bold">Your team is registered!</h1>
              <p className="text-muted-foreground mt-2">
                {showPending
                  ? "Your registration is saved — payment is still confirming in the background. You'll get a receipt by email."
                  : "We've emailed your coach a magic link to manage the roster. Payment details are below."}
              </p>
            </>
          )}
        </div>

        {!isLoading && !showSpinner && registrations && registrations.length > 0 && (
          <EventVerificationNotice eventId={registrations[0].events?.id} className="max-w-2xl mx-auto" />
        )}

        {isLoading || showSpinner ? (
          <div className="flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {(registrations ?? []).length > 1 && (
              <p className="text-sm text-muted-foreground">
                {registrations.length} teams registered in this order.
              </p>
            )}
            {(registrations ?? []).map((r: any) => {
              const isLater =
                r.payment_method_chosen && r.payment_method_chosen !== "online";
              const isPaid = r.payment_status === "paid";
              const summary = (r.extras_summary ?? {}) as any;
              const constraints = (summary.constraints ?? []) as any[];
              const extras = ((summary.extras ?? []) as any[]).filter(
                (e: any) => Number(e.qty || 0) > 0
              );
              const basePrice = Number(
                summary.base_price ??
                  Math.max(
                    0,
                    Number(r.total_amount_due ?? 0) +
                      Number(r.discount_total ?? 0) -
                      Number(r.special_requests_fee ?? 0) -
                      Number(r.extras_total ?? 0) -
                      Number(r.scheduling_tier_fee ?? 0)
                  )
              );
              const money = (n: number) => `$${Number(n || 0).toFixed(2)}`;
              const row = (label: string, amount: string, key: string) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{label}</span>
                  <span>{amount}</span>
                </div>
              );
              return (
                <Card key={r.id}>
                  <CardContent className="pt-6">
                    <div className="font-semibold">
                      {r.resolved_team_name ?? storedRegistrationTeamName(r)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {r.events?.name}
                      {r.pool ? ` · ${r.pool}` : ""}
                    </div>

                    <div className="mt-3 space-y-1 border-t border-border pt-3">
                      {row("Entry fee", money(basePrice), "base")}
                      {constraints.map((c: any, i: number) =>
                        row(
                          `Scheduling: ${c.template_label ?? c.constraint_type ?? "Request"}`,
                          money(c.fee ?? 0),
                          `c-${i}`
                        )
                      )}
                      {Number(r.scheduling_tier_fee ?? 0) > 0 &&
                        row(
                          `Scheduling tier (${r.scheduling_tier})`,
                          money(r.scheduling_tier_fee),
                          "tier"
                        )}
                      {extras.map((e: any, i: number) =>
                        row(
                          `${e.name ?? "Extra"} ×${e.qty}`,
                          money(Number(e.price || 0) * Number(e.qty || 0)),
                          `e-${i}`
                        )
                      )}
                      {Number(r.discount_total ?? 0) > 0 &&
                        row(
                          summary.discount_policy?.name
                            ? `Discount (${summary.discount_policy.name})`
                            : "Discount",
                          `-${money(r.discount_total)}`,
                          "disc"
                        )}
                    </div>

                    {Number(r.processing_fee ?? 0) > 0 && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-muted-foreground">
                          Card processing fee (3%)
                        </span>
                        <span>{money(r.processing_fee)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm font-semibold mt-2 border-t border-border pt-2">
                      <span>Team total</span>
                      <span>
                        {money(
                          Number(r.total_amount_due ?? 0) +
                            Number(r.processing_fee ?? 0)
                        )}
                        {isPaid && (
                          <span className="ml-2 text-emerald-600 font-medium">
                            ✓ Paid
                          </span>
                        )}
                      </span>
                    </div>

                    {isLater && (
                      <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded text-sm">
                        <p className="font-medium">
                          Pay later via {r.payment_method_chosen}
                        </p>
                        {r.payment_due_at && (
                          <p className="text-xs">
                            Due by {new Date(r.payment_due_at).toLocaleDateString()}
                          </p>
                        )}
                        {r.events?.pay_later_instructions && (
                          <div className="mt-2">
                            <RichTextDisplay
                              html={r.events.pay_later_instructions}
                              className="text-xs"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {(registrations ?? []).length > 0 && (
              <Card>
                <CardContent className="pt-6 flex justify-between font-semibold">
                  <span>Order total</span>
                  <span>
                    $
                    {registrations
                      .reduce(
                        (s: number, r: any) =>
                          s +
                          Number(r.total_amount_due ?? 0) +
                          Number(r.processing_fee ?? 0),
                        0
                      )
                      .toFixed(2)}
                  </span>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-center flex-wrap">
          <Button onClick={() => goToRecruitLook()}>Go to my RecruitLook dashboard</Button>
          <Link to={`/events/${slug}`}>
            <Button variant="outline">Back to event</Button>
          </Link>
          <Link to="/events">
            <Button variant="outline">Browse other events</Button>
          </Link>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Manage your team and roster on RecruitLook. Receipt download coming soon.
        </p>
      </div>
    </div>
  );
}
